import { FC, FormEvent, useState } from 'react'
import axios from 'axios'
import { useQueryClient } from 'react-query'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'
import { ButtonType } from '../../../../../../../_metronic/helpers'
import { Button, InfoAlert, LoadingIndicator, Radio } from '../../../../../../components'
import { ResultadoSolicitacaoExameImagem } from '../../../../atendimento/core/_models'
import { ErroExameImagem, traduzirErroExameImagem } from './erros'
import { ErroExameImagemAlerta } from './ErroExameImagemAlerta'
import {
  chaveExamesImagem,
  useDispositivosExameImagem,
  useSolicitarExameImagem,
} from './useExamesImagem'

type Props = {
  id: string
  idAtendimento: number
  onSolicitado: (resultado: ResultadoSolicitacaoExameImagem) => void
}

export const SolicitarExameImagemForm: FC<Props> = ({ id, idAtendimento, onSolicitado }) => {
  const { t } = useT()
  const queryClient = useQueryClient()
  const dispositivos = useDispositivosExameImagem(idAtendimento, true)
  const solicitar = useSolicitarExameImagem(idAtendimento)
  const [selecionado, setSelecionado] = useState<number | undefined>(undefined)
  const [erro, setErro] = useState<ErroExameImagem | undefined>(undefined)

  const lista = dispositivos.data ?? []
  const idSelecionado =
    selecionado !== undefined && lista.some((a) => a.id === selecionado)
      ? selecionado
      : lista.length === 1
      ? lista[0].id
      : undefined
  const semPermissao =
    axios.isAxiosError(dispositivos.error) && dispositivos.error.response?.status === 403

  const enviar = (evento: FormEvent) => {
    evento.preventDefault()
    if (idSelecionado === undefined || solicitar.isLoading) return
    setErro(undefined)
    solicitar.mutate(idSelecionado, {
      onSuccess: (resultado) => onSolicitado(resultado),
      onError: (error) => {
        const traduzido = traduzirErroExameImagem(error, 'solicitar', t)
        setErro(traduzido)
        if (traduzido.acao === 'recarregarDispositivos') {
          setSelecionado(undefined)
          dispositivos.refetch()
        } else if (traduzido.acao === 'recarregarLista') {
          queryClient.invalidateQueries(chaveExamesImagem(idAtendimento))
        }
      },
    })
  }

  const conteudo = () => {
    if (dispositivos.isLoading) {
      return <LoadingIndicator message={t('exames_imagem.form.carregando')} />
    }
    if (semPermissao) {
      return (
        <InfoAlert variant='danger' role='alert' size='sm'>
          {t('exames_imagem.sem_permissao')}
        </InfoAlert>
      )
    }
    if (dispositivos.isError) {
      return (
        <InfoAlert
          variant='danger'
          role='alert'
          size='sm'
          action={
            <Button variant='tertiary' size='btn-sm' onClick={() => dispositivos.refetch()}>
              {t('exames_imagem.tentar_novamente')}
            </Button>
          }
        >
          {t('exames_imagem.form.erro_carregar')}
        </InfoAlert>
      )
    }
    if (lista.length === 0) {
      return <p className='text-gray-700 mb-0'>{t('exames_imagem.sem_dispositivo')}</p>
    }
    return (
      <fieldset disabled={solicitar.isLoading}>
        <legend className='fs-6 fw-bold text-gray-900 mb-3'>
          {t('exames_imagem.form.legenda')}
        </legend>
        <Radio
          id={`${id}-dispositivo`}
          isColumn
          value={idSelecionado}
          onChange={setSelecionado}
          disabled={solicitar.isLoading}
          options={lista.map((dispositivo) => ({
            id: dispositivo.id,
            value: dispositivo.id,
            descricao: (
              <>
                <span className='d-block fw-bold text-gray-900'>
                  {dispositivo.descricao ||
                    dispositivo.dispositivo?.descricao ||
                    dispositivo.aeTitle}
                </span>{' '}
                <span className='d-block fs-7 fw-normal text-gray-700'>
                  {t('exames_imagem.form.ae_title', { aeTitle: dispositivo.aeTitle })}
                  {' · '}
                  {t('exames_imagem.form.telekit', {
                    identificador: dispositivo.telekit.identificador,
                  })}
                  {' · '}
                  {dispositivo.telekit.empresa.nome}
                </span>
              </>
            ),
          }))}
        />
      </fieldset>
    )
  }

  const podeEnviar = !semPermissao && idSelecionado !== undefined && !solicitar.isLoading

  return (
    <form id={id} className='border border-gray-300 rounded p-4 mb-5' onSubmit={enviar} noValidate>
      {conteudo()}
      {erro && <ErroExameImagemAlerta erro={erro} />}
      <div className='d-flex justify-content-end mt-3'>
        <Button
          type={ButtonType.Submit}
          variant='secondary-outline'
          size='btn-sm'
          disabled={!podeEnviar}
        >
          {solicitar.isLoading ? (
            <LoadingIndicator message={t('exames_imagem.form.enviando')} />
          ) : (
            t('exames_imagem.form.enviar')
          )}
        </Button>
      </div>
    </form>
  )
}
